import compact from 'lodash/compact';
import keyBy from 'lodash/keyBy';
import each from 'lodash/each';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import startsWith from 'lodash/startsWith';
import values from 'lodash/values';
import reduce from 'lodash/reduce';
import some from 'lodash/some';

import loglevel from 'loglevel';

import Folder from '../legacy_models/folder.js';

export const FOLDER_TYPE_ALL = 'ALL';
export const FOLDER_TYPE_ARCHIVED = 'ARCHIVED';
export const FOLDER_TYPE_NORMAL = 'NORMAL';
export const FOLDER_TYPE_NON_SUPER_ROOT = 'NON_SUPER_ROOT';
export const FOLDER_TYPE_NON_SUPER_NO_FOLDER = 'NO_FOLDER';
export const FOLDER_TYPE_SUPER_ROOT = 'SUPER_ROOT';
export const FOLDER_TYPE_SUPER_NO_FOLDER = 'SUPER_NO_FOLDER';

// Folder Model --> backbone model
// Folder Obj --> json of model (it has "parent_id" prop; it has )
// Folder node --> part of a "tree" (it has "children" prop)

const SERIALIZED_CID_PREFIX = 'f:';

export const getCidFromFolderType = (type)=> {
  if (type == FOLDER_TYPE_NORMAL) {
    throw new Error(`Folder type ${FOLDER_TYPE_NORMAL} does not fully specify a cid`);
  }
  return `${SERIALIZED_CID_PREFIX}${type}`;
};

export const getFolderTypeFromCid = (cid)=>
  isNumber(cid) ?
    FOLDER_TYPE_NORMAL
  : startsWith(cid, SERIALIZED_CID_PREFIX) ?
    cid.slice(SERIALIZED_CID_PREFIX.length)
  :
    (()=> { throw new Error(`Not a valid cid ${cid}`); })();


const makeFolderNode = ({  type, id, ...data })=>
  ({
    ...data,
    children: [],
    cid: id ? id : getCidFromFolderType(type),
    id,
    type
  });


export function makeFolderTreeFromModels(folderModels, opts) {
  return makeFolderTreeFromFolderObjs(map(folderModels, f => f.toJSON()), opts);
}

export function makeFolderTreeFromFolderObjs(
  folderObjs, 
  { 
    includeAll=true,
    includeNoFolder=false, 
    includeMyRoot=false,
    includeSuperRoot=false 
  }={}
) {

  const treeChildren = [];

  let rootChildren, superFolderChildren, nonSuperFolderChildren;

  if (includeAll) {
    let allFolder = makeFolderNode({
      name: 'All',
      type: FOLDER_TYPE_ALL
    });
    treeChildren.push(
      allFolder
    );
    rootChildren = allFolder.children;
  } else {
    rootChildren = treeChildren;
  }

  if (includeMyRoot) {
    var nonSuperFolder = makeFolderNode({
      name: 'My Folders',
      type: FOLDER_TYPE_NON_SUPER_ROOT
    });
    rootChildren.push(
      nonSuperFolder
    );
    nonSuperFolderChildren = nonSuperFolder.children;
  
  } else {
    nonSuperFolderChildren = rootChildren;
  }

  if (includeSuperRoot) {
    var superFolder = makeFolderNode({
      name: 'Zoomforth Folders',
      type: FOLDER_TYPE_SUPER_ROOT
    });
    rootChildren.push(
      superFolder
    );
    superFolderChildren = superFolder.children;
  
  } else {
    superFolderChildren = rootChildren;
  }

  if (includeNoFolder) {
    if (includeMyRoot) {
      nonSuperFolderChildren.push(makeFolderNode({
        name: 'No Folder',
        type: FOLDER_TYPE_NON_SUPER_NO_FOLDER
      }));
    }
    if (includeSuperRoot) {
      superFolderChildren.push(makeFolderNode({
        name: 'No Folder',
        type: FOLDER_TYPE_SUPER_NO_FOLDER
      }));
    }
  }

  const byId = keyBy(
    map(folderObjs, (f)=> makeFolderNode({ ...f, type:FOLDER_TYPE_NORMAL })), 
    'id'
  );

  const handleNoParent = (folder)=> {
    if (folder.is_super) {
      superFolderChildren.push(folder);
    } else {
      nonSuperFolderChildren.push(folder);
    }
  };

  each(sortBy(byId, (folder)=> ((folder.name)||'').toLowerCase()), folder => {

    if (!folder.parent_id) {
      // Top level folder
      if (!('parent_id' in folder)) { 
        delete folder.parent_id; // Delete null parent_id entries
      }
      handleNoParent(folder);

    } else if (!byId[folder.parent_id]) {
      // Folder's parent doesn't exist!
      loglevel.warn(`Folder with id=${folder.id} and name=${folder.name}
                       is missing its parent with parent_id=${folder.parent_id}. Will pretend it is root folder.`);
      handleNoParent(folder);

    } else {
      let parent = byId[folder.parent_id];
      delete folder.parent_id;
      parent.children.push(folder);
    }
  });

  return treeChildren;
}



/**

  Given a folder tree type object `tree` run the `processor` call
  back function on every node in the tree.

  Set the node returned by the processor as the new node in the tree.

*/
export function rebuildTree(tree, processor) {

  function rebuildNode(node, nodeIndex) {

    const { children, ...newNode } = node;
    // Note that because the recursion is depth-first
    // the processor only sees children which have been pre-processed
    newNode.children = compact(map(children, rebuildNode));
    return processor(newNode, nodeIndex);
  }
  return compact(map(tree, rebuildNode));
}


export function makeFolderObjsFromFolderTree(tree) {
  const folderObjs = [];

  function flatten(node, parentCid) {

    const { children, ...folderObj } = node;

    each(children, (child)=> {
      flatten(child, folderObj.cid);
    });
    folderObj.parentCid = parentCid || null;
    folderObjs.push(folderObj);
  }

  each(tree, (node)=> {
    flatten(node);
  });

  return folderObjs;
}


export function makeFoldersFromFolderTree(tree) {
  const folderObjs = makeFolderObjsFromFolderTree(tree);
  return map(
    folderObjs, 
    (obj)=> new Folder(obj)
  );
}


/**
  Given a node `node` check all of its descendants (any level of depth of 
  children with the `testFunc`. If any testFunc value returns `truthy`
  then this function returns `true`.

*/
export function matchesAnyDescendants(folderNode, testFunc) {
  function matches(children) {
    if (some(children, testFunc)) {
      return true;
    }
    return !!find(children, (child)=> matches(child.children));
  }
  return matches(folderNode.children);
}

/**
  Given folderObjs

  return `[<GreatGrandparent>, <Grandparent>, <Parent>]`, does not include the `folderObj`` at `folderKey`
*/
export function getOrderedAncestorFolderObjs(folderObjs, keyName, folderKey) {
  const folderObjByKey = keyBy(folderObjs, keyName);
  const folderObj = folderObjByKey[folderKey]; // Can be Folder model or id
  const ancestors = [];
  var currentFolder = folderObj;
  while (currentFolder.parentCid) {
    currentFolder = folderObjByKey[currentFolder.parentCid];
    ancestors.unshift(currentFolder);
  }
  return ancestors;
}



export function getPagesByFolderCid(pageModels) {
  const pagesByFolderCid = {};
  each(pageModels, (page)=> {
    const folderCid = (
        page.get('folder_id') ?
        page.get('folder_id')
      
      : page.get('is_super') ?
        getCidFromFolderType(FOLDER_TYPE_SUPER_NO_FOLDER)
      :
        getCidFromFolderType(FOLDER_TYPE_NON_SUPER_NO_FOLDER)

    );
    if (!folderCid) { 
      throw new Error('Unexpected nil folderCid');
    }
    pagesByFolderCid[folderCid] = (pagesByFolderCid[folderCid] || []).concat([page]);
  });

  return pagesByFolderCid;
}


export function getPageCountByFolderCid(pagesByFolderCid) {
  return reduce(
    pagesByFolderCid,
    (accum, pages, folderCid)=>
      ({
        ...accum,
        [folderCid]: pages.length
      }),
    {}
  );
}

/**
  
  Return the "cumulative" set of pages for each folder, keyed by that folder's cid.

  Where "cumulative" means the union of the sets of pages which directly belong to each folder which
  is a descendant of the the folder in question.

*/
export function getCumPagesByFolderCid(folderObjByCid, pagesByFolderCid) {

  const folderObjs = values(folderObjByCid);

  const cumPagesByFolderCid = {};

  const orderedAncestorFolderObjsByCid = getOrderedAncestorFolderObjsByCid(folderObjs);

  // Calculate the "cumulative" pages for a folder, by having each folder
  // add its set of pages to each of its ancestors.
  each(folderObjs, (folderObj)=> {
    const folderPages = pagesByFolderCid[folderObj.cid] || [];
    const appendPagesToFolderCid = (folderCid)=> {
      cumPagesByFolderCid[folderCid] = (cumPagesByFolderCid[folderCid] || [])
          .concat(folderPages);
    };
    appendPagesToFolderCid(folderObj.cid); // Append pages for *this* folder
    each( // Append pages for all this folder's ancestors
      orderedAncestorFolderObjsByCid[folderObj.cid],
      (ancestor)=> {
        appendPagesToFolderCid(ancestor.cid);
      }
    );
  });
  return cumPagesByFolderCid;
}


export function getOrderedAncestorFolderObjsByCid(folderObjs) {
  return reduce(
    folderObjs,
    (accum, folderObj)=>
      ({
        ...accum,
        [folderObj.cid]: getOrderedAncestorFolderObjs(folderObjs, 'cid', folderObj.cid)
      }),
    {}
  );
}


export function getCumPageCountByFolderCid(cumPagesByFolderCid) {
  return reduce(
    cumPagesByFolderCid,
    (accum, pages, folderCid)=>
      ({
        ...accum,
        [folderCid]: pages.length
      }),
    {}
  );
}
